import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import { fetchSite, getToken, updateProduct, uploadFile } from './api';
import ProductForm from './ProductForm';
import { AdminImagePreview } from './adminUi';

function ensureProductsShape(site) {
  const d = DEFAULT_SITE.products;
  if (site.products && Array.isArray(site.products.items)) return site;
  return {
    ...site,
    products: {
      sectionLabel: d.sectionLabel,
      sectionTitle: d.sectionTitle,
      intro: d.intro,
      items: [...(d.items || [])],
    },
  };
}

export default function ProductEdit() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loadState, setLoadState] = useState('loading');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    let cancelled = false;
    setLoadState('loading');

    function applyData(data) {
      if (cancelled) return;
      const shaped = ensureProductsShape(data);
      const decodedId = productId ? decodeURIComponent(productId) : '';
      const row = shaped.products.items.find((p) => p.id === decodedId);
      if (!row) {
        setProduct(null);
        setLoadState('notfound');
        return;
      }
      setProduct({ ...row, description: row.description || '' });
      setLoadState('ready');
    }

    fetchSite().then(applyData).catch(() => applyData(JSON.parse(JSON.stringify(DEFAULT_SITE))));
    return () => {
      cancelled = true;
    };
  }, [navigate, productId]);

  async function onUpload(file) {
    if (!file) return;
    try {
      const { url } = await uploadFile(file);
      setProduct((p) => (p ? { ...p, image: url } : p));
    } catch (e) {
      setErr(e.message || 'Upload failed');
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!product || !productId) return;
    setErr('');
    setSaving(true);
    try {
      await updateProduct(decodeURIComponent(productId), product);
      navigate('/admin/dashboard/products');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loadState === 'loading') {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-neutral-600">Loading…</div>
    );
  }

  if (loadState === 'notfound' || !product) {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-8 md:px-8">
        <p className="text-neutral-700">Product not found.</p>
        <Link to="/admin/dashboard/products" className="mt-4 inline-block text-sm font-semibold text-brand-lime">
          ← Back to products
        </Link>
      </div>
    );
  }

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <Link
            to="/admin/dashboard/products"
            className="text-xs font-medium text-brand-olive hover:underline"
          >
            ← Back to products
          </Link>
          <h2 className="mt-1 text-lg font-bold text-neutral-900 md:text-xl">Edit product</h2>
        </div>
      </header>

      <main className="min-h-0 w-full min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-6 md:px-8 md:py-8">
        {err ? <p className="mb-4 text-sm text-red-600">{err}</p> : null}

        <form onSubmit={handleSave} className="w-full max-w-none rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
          <AdminImagePreview src={product.image} alt={product.alt} emptyLabel="Preview" />

          <ProductForm product={product} onChange={setProduct} onUploadImage={onUpload} />

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-brand-forest px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <Link
              to="/admin/dashboard/products"
              className="rounded-lg border border-neutral-300 bg-white px-5 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Cancel
            </Link>
          </div>
        </form>
      </main>
    </>
  );
}
